import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import QuizCard from '../components/QuizCard';
import axios from 'axios';
import { AlertCircle, Trophy, Clock, Crown, Star, Flame } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CountdownTimer = ({ scheduleTime }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const target = new Date(scheduleTime).getTime();
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft('LIVE NOW!');
        return;
      }

      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      parts.push(`${hours}h ${mins}m ${secs}s`);
      setTimeLeft(parts.join(' '));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [scheduleTime]);

  return (
    <span className="font-mono font-bold text-[#FF6B6B]" data-testid="countdown-timer">
      {timeLeft}
    </span>
  );
};

const MegaPoolCard = ({ pool, onJoin, onViewPrizes }) => {
  const prizeColors = {
    10000: 'from-[#0EA5E9] to-[#0284C7]',
    30000: 'from-[#FBBF24] to-[#F59E0B]',
    100000: 'from-[#FF6B6B] to-[#EE5A52]'
  };

  const gradientClass = prizeColors[pool.prize_pool] || 'from-[#0EA5E9] to-[#0284C7]';
  const fillPercentage = (pool.current_participants / pool.max_participants) * 100;

  return (
    <div className="relative quiz-card overflow-hidden" data-testid={`mega-pool-${pool.id}`}>
      {/* Top Banner */}
      <div className={`bg-gradient-to-r ${gradientClass} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-6 h-6 text-white" />
            <span className="text-white font-bold text-lg uppercase tracking-wide" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {pool.subject}
            </span>
          </div>
          <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-white font-bold text-sm">MEGA</span>
          </div>
        </div>
        <p className="text-white/80 text-sm mt-1">{pool.topic}</p>
      </div>

      <div className="p-6">
        {/* Prize Pool */}
        <div className="text-center mb-4">
          <p className="text-sm text-[#6B7280] font-medium">Prize Pool</p>
          <p className="text-4xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {pool.prize_pool.toLocaleString()} Pts
          </p>
        </div>

        {/* Countdown */}
        {pool.schedule_time && (
          <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-red-50 rounded-xl">
            <Clock className="w-4 h-4 text-[#FF6B6B]" />
            <span className="text-sm text-[#6B7280]">Starts in:</span>
            <CountdownTimer scheduleTime={pool.schedule_time} />
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2 bg-slate-50 rounded-xl">
            <p className="text-xs text-[#6B7280]">Entry</p>
            <p className="font-bold text-[#111827]">{pool.entry_fee} pts</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-xl">
            <p className="text-xs text-[#6B7280]">Spots</p>
            <p className="font-bold text-[#111827]">{pool.max_participants}</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-xl">
            <p className="text-xs text-[#6B7280]">Winners</p>
            <p className="font-bold text-[#10B981]">{Math.floor(pool.max_participants * 0.5)}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 bg-gradient-to-r ${gradientClass} transition-all duration-500 shimmer`}
              style={{ width: `${fillPercentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-1">
            <p className="text-xs text-[#6B7280]">{pool.current_participants} joined</p>
            <p className="text-xs text-[#6B7280]">{pool.max_participants - pool.current_participants} spots left</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => onViewPrizes(pool)}
            className="flex-1 px-4 py-3 bg-white text-[#0EA5E9] border-2 border-[#0EA5E9] rounded-2xl font-bold hover:bg-[#0EA5E9]/5 transition-all text-sm"
            data-testid={`view-prizes-${pool.id}`}
          >
            Prize Table
          </button>
          <button
            onClick={() => onJoin(pool)}
            className={`flex-1 px-4 py-3 bg-gradient-to-r ${gradientClass} text-white rounded-2xl font-bold shadow-lg hover:shadow-xl active:translate-y-1 transition-all text-sm`}
            data-testid={`join-mega-${pool.id}`}
          >
            Join • {pool.entry_fee} pts
          </button>
        </div>
      </div>
    </div>
  );
};

const PrizeModal = ({ pool, distribution, onClose }) => {
  if (!pool || !distribution) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="prize-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Prize Distribution
              </h3>
              <p className="text-white/80 text-sm">{pool.subject}</p>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-xs">Total Prize</p>
              <p className="text-2xl font-black text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {pool.prize_pool.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-4 text-sm text-[#6B7280]">
            <span>Top {distribution.winners_percent || 3}% win ({distribution.winners_count} of {distribution.max_participants})</span>
            <span className="text-[#10B981] font-bold">{distribution.platform_fee_percent || 15}% platform fee</span>
          </div>

          <div className="space-y-2">
            {distribution.distribution.map((tier, idx) => {
              const isTop = tier.rank_start <= 3;
              const rankLabel = tier.rank_start === tier.rank_end
                ? `#${tier.rank_start}`
                : `#${tier.rank_start} - #${tier.rank_end}`;

              return (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-xl border-2 ${
                    tier.rank_start === 1 ? 'leaderboard-gold border-yellow-300' :
                    tier.rank_start === 2 ? 'leaderboard-silver border-gray-200' :
                    tier.rank_start === 3 ? 'leaderboard-bronze border-orange-200' :
                    'bg-white border-slate-100'
                  }`}
                  data-testid={`prize-tier-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    {tier.rank_start === 1 && <Trophy className="w-5 h-5 text-yellow-600" />}
                    {tier.rank_start === 2 && <Star className="w-5 h-5 text-gray-500" />}
                    {tier.rank_start === 3 && <Star className="w-5 h-5 text-orange-500" />}
                    {tier.rank_start > 3 && <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-[#6B7280]">{rankLabel}</span>}
                    <span className={`font-bold ${isTop ? 'text-[#111827]' : 'text-[#6B7280]'}`}>
                      Rank {rankLabel}
                    </span>
                  </div>
                  <span className="font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {tier.prize.toLocaleString()} pts
                  </span>
                </div>
              );
            })}
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 px-6 py-3 bg-[#111827] text-white rounded-2xl font-bold hover:bg-[#1F2937] transition-colors"
            data-testid="close-prize-modal"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

const QuizPools = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prizeModal, setPrizeModal] = useState(null);
  const [prizeDistribution, setPrizeDistribution] = useState(null);

  const fetchPools = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quiz-pools`);
      setPools(data);
    } catch (err) {
      setError('Failed to load quiz pools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPools();
    // Auto-refresh pools every 30 seconds so new pools pop up
    const interval = setInterval(fetchPools, 30000);
    return () => clearInterval(interval);
  }, [fetchPools]);

  const megaPools = useMemo(() => pools.filter(p => p.pool_type === 'mega'), [pools]);
  const regularPools = useMemo(() => pools.filter(p => p.pool_type !== 'mega'), [pools]);

  const handleJoinPool = async (pool, action) => {
    // If already joined and pool is full, go to quiz
    if (action === 'start') {
      navigate(`/quiz/${pool.id}`);
      return;
    }

    if (user.points < pool.entry_fee) {
      toast.error('Insufficient points! Buy more points to continue.');
      return;
    }
    if (pool.current_participants >= pool.max_participants) {
      toast.error('This pool is full!');
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/api/quiz-pools/${pool.id}/join`,
        {}
      );
      await refreshUser();

      if (data.pool_full) {
        toast.success('Pool is full! Quiz is starting now!');
        navigate(`/quiz/${pool.id}`);
      } else {
        toast.success('Joined! Quiz will start when the pool fills up.');
        fetchPools(); // Refresh pools to show updated state
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to join quiz pool';
      if (message.includes('already enrolled')) {
        // User already joined, check if pool is full
        if (pool.current_participants >= pool.max_participants) {
          navigate(`/quiz/${pool.id}`);
        } else {
          toast.info('You are already in this pool. Waiting for it to fill up.');
        }
      } else {
        toast.error(message);
      }
    }
  };

  const handleViewPrizes = async (pool) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/quiz-pools/${pool.id}/prize-distribution`
      );
      setPrizeModal(pool);
      setPrizeDistribution(data);
    } catch (err) {
      toast.error('Failed to load prize distribution');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
            <p className="mt-4 text-[#6B7280]">Loading quiz pools...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="quiz-pools-page">
      <Header />

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Mega Pools Section */}
        {megaPools.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Flame className="w-8 h-8 text-[#FF6B6B]" />
              <h2
                className="text-3xl sm:text-4xl font-black text-[#111827]"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="mega-pools-heading"
              >
                Mega Contests
              </h2>
              <div className="px-3 py-1 bg-[#FF6B6B] text-white rounded-full text-xs font-bold animate-pulse">
                HOT
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {megaPools.map((pool) => (
                <MegaPoolCard
                  key={pool.id}
                  pool={pool}
                  onJoin={handleJoinPool}
                  onViewPrizes={handleViewPrizes}
                />
              ))}
            </div>
          </div>
        )}

        {/* Regular Pools */}
        <div className="mb-12">
          <h2
            className="text-3xl sm:text-4xl font-black text-[#111827] mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Quiz Pools
          </h2>
          <p className="text-base text-[#6B7280] mb-8">
            Join a quiz pool, compete with aspirants, and win points!
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {regularPools.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#6B7280] text-lg">No regular quiz pools available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPools.map((pool) => (
              <QuizCard key={pool.id} pool={pool} onJoin={handleJoinPool} userJoined={pool.participants?.includes(user?._id)} />
            ))}
          </div>
        )}
      </main>

      {/* Prize Distribution Modal */}
      {prizeModal && prizeDistribution && (
        <PrizeModal
          pool={prizeModal}
          distribution={prizeDistribution}
          onClose={() => { setPrizeModal(null); setPrizeDistribution(null); }}
        />
      )}
    </div>
  );
};

export default QuizPools;
